import type { HistogramOverlay } from '../../statistics/measures/measures/measures-highlight.util';
import { CHART_COLORS } from './chart-styling.util';

// ---------------------------------------------------------------------------
// Number formatting
// ---------------------------------------------------------------------------

/** Format a number for display: integers as-is, decimals to 2 places with trailing zeros stripped. */
export function fmt(v: number): string {
  if (Number.isInteger(v)) {
    return String(v);
  }
  return parseFloat(v.toFixed(2)).toString();
}

/** Axis label formatter that cleans up floating-point artifacts (e.g. 0.0000001 -> 0). */
export function fmtAxisLabel(v: number): string {
  if (Math.abs(v) < 1e-10) {
    return '0';
  }
  if (Number.isInteger(v)) {
    return String(v);
  }
  return parseFloat(v.toPrecision(12)).toString();
}

// ---------------------------------------------------------------------------
// Rounding
// ---------------------------------------------------------------------------

/** Round a value to the given number of decimal places. */
export function roundTo(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}

// ---------------------------------------------------------------------------
// Custom series render API
// ---------------------------------------------------------------------------

/** Minimal typing for the ECharts custom series `renderItem` API. */
export interface RenderApi {
  value: (dim: number) => number;
  coord: (data: number[]) => number[];
  size?: (data: number[]) => number[];
}

// ---------------------------------------------------------------------------
// Overlay series (markLine / markArea from measure hover)
// ---------------------------------------------------------------------------

type OverlayAxis = 'x' | 'y';

/**
 * Build a silent overlay series that renders markLines and markAreas
 * from the statistics store's highlight overlay.
 *
 * @param overlay  The highlight overlay containing markLines and markAreas.
 * @param axis     Which axis the overlay values apply to (`'x'` for horizontal
 *                 charts like box-plot / dot-plot / strip-plot, `'y'` for
 *                 vertical charts like deviation-plot). Defaults to `'x'`.
 */
export function buildOverlaySeries(overlay: HistogramOverlay, axis: OverlayAxis = 'x'): Record<string, unknown> {
  const axisKey = axis === 'x' ? 'xAxis' : 'yAxis';
  const seriesType = axis === 'x' ? 'scatter' : 'line';
  const labelPosition = axis === 'x' ? 'end' : 'insideEndTop';
  const areaLabelPosition = axis === 'x' ? 'top' : 'insideTop';
  const labelOffset = axis === 'x' ? [0, -10] : undefined;

  return {
    type: seriesType,
    data: [],
    silent: true,
    ...(seriesType === 'scatter' ? { symbolSize: 0 } : {}),
    tooltip: { show: false },
    markLine: {
      symbol: 'none',
      animation: false,
      data: overlay.markLines.map((l) => ({
        [axisKey]: l.value,
        label: {
          formatter: l.label,
          position: labelPosition,
          ...(labelOffset ? { offset: labelOffset } : {}),
          fontSize: 10,
          color: l.color,
        },
        lineStyle: {
          color: l.color,
          type: l.lineStyle,
          width: 1.5,
        },
      })),
    },
    markArea: {
      animation: false,
      data: overlay.markAreas.map((a) => [
        {
          [axisKey]: a.from,
          itemStyle: { color: a.color },
          label: {
            show: !!a.label,
            position: areaLabelPosition,
            formatter: a.label,
            color: CHART_COLORS.label,
            fontSize: 10,
          },
        },
        {
          [axisKey]: a.to,
        },
      ]),
    },
  };
}
